import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Shield, Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';
import { buildQrUrl } from '../features/shared/WizardComponents';
import { ProductPrintView } from '../features/thermal-insulations/ProductPrintView';
import { AirBarrierPrintView } from '../features/air-barriers/AirBarrierPrintView';
import { WeatherBarrierPrintViewInline } from '../features/weather-barriers/WeatherBarriersSection';
import { RoofingPrintView } from '../features/roofing/RoofingPrintView';
import { FlashingPrintView } from '../features/flashing-sheet-metal/FlashingPrintView';
import { JointProtectionPrintViewInline } from '../features/joint-protection/JointProtectionPrintView';

const API = process.env.REACT_APP_BACKEND_URL || '';

const D7_LABELS = {
  'thermal-insulation': 'Thermal Insulation',
  'air-barrier': 'Air Barrier',
  'weather-barrier': 'Weather Barrier',
  'roofing': 'Roofing & Flashing',
  'flashing': 'Flashing & Sheet Metal',
  'joint-protection': 'Joint Protection',
};

const D8_LABELS = {
  'window': 'Window',
  'cw-panel': 'Curtain Wall Panel',
  'sf-panel': 'Storefront Panel',
  'door': 'Door',
  'shower': 'Shower',
  'mirror': 'Mirror',
};

const D26_LABELS = {
  'electrical-panel': 'Electrical Panel',
  'electrical-equipment': 'Electrical Equipment',
  'electrical-device': 'Electrical Device',
};

function getProductImage(product) {
  return product.imageCloudinary?.urls?.medium || product.imageUrl || null;
}

const fmtDim = (v) => v != null && v !== '' ? `${v}"` : 'N/A';
const fmtVal = (v) => v != null && v !== '' ? String(v) : 'N/A';

// ═══════════════════════════════════════════════════════════════
// Field row — reusable for grid-based info sections
// ═══════════════════════════════════════════════════════════════
function InfoField({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <dt className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-slate-900 font-medium mt-0.5">{value || '-'}</dd>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Windows Public Card — content parity with Print Label PDF,
// UX structure from Electrical public page
// ═══════════════════════════════════════════════════════════════
function WindowPublicCard({ data }) {
  const qrUrl = data.qrToken ? buildQrUrl(data.qrToken) : '';
  const wt = data.windowType || {};
  const sc = data.subContractor;
  const csiTitle = data.csiItemTitle || 'Windows';
  const lites = data.lites || [];

  return (
    <>
      {/* ── Brand bar ── */}
      <div className="bg-white border-b" data-testid="public-brand-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center">
          <Logo size="sm" showText={false} />
          <span className="text-sm font-semibold text-slate-700 ml-2">Specard Inc.</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-4 sm:py-6 px-4 sm:px-6">
        {/* ── Title + Tag + QR ── */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="public-page-title">Window Card</h1>
            <p className="text-base font-semibold text-orange-600 mt-1" data-testid="public-entity-tag">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide mr-2">Window Label</span>
              {data.label}
            </p>
          </div>
          {qrUrl && (
            <div className="shrink-0 ml-4" data-testid="public-qr-code">
              <QRCodeSVG value={qrUrl} size={96} level="L" />
              <p className="text-[10px] text-slate-400 text-center mt-1">Scan for Details</p>
            </div>
          )}
        </div>

        {/* ── Project Information + Sub Contractor — side by side ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Project Information */}
          {(data.projectName || data.projectAddress) && (
            <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6" data-testid="public-project-info">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Project Information</h2>
              <div className="space-y-2">
                <InfoField label="Project" value={data.projectName} testId="public-projectName" />
                {data.projectNumber && <InfoField label="Project Number" value={data.projectNumber} testId="public-projectNumber" />}
                <InfoField label="Address" value={data.projectAddress} testId="public-projectAddress" />
              </div>
            </div>
          )}

          {/* Sub Contractor */}
          <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6" data-testid="public-sub-contractor">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Sub Contractor ({csiTitle})</h2>
            {sc && sc.companyName ? (
              <div className="space-y-2">
                <InfoField label="Company" value={sc.companyName} testId="sc-company" />
                {sc.phone && <InfoField label="Telephone" value={sc.phone} testId="sc-phone" />}
                {sc.email && <InfoField label="Email" value={sc.email} testId="sc-email" />}
              </div>
            ) : (
              <p className="text-sm text-slate-400" data-testid="sc-not-assigned">Not assigned</p>
            )}
          </div>
        </div>

        {/* ── Window Information ── */}
        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="public-window-type-info">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Window Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Info fields — 1/2 (50%) */}
            <div>              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {data.windowTypeLabel && <InfoField label="Window Type" value={data.windowTypeLabel} testId="wt-typeLabel" />}
                {data.typeName && <InfoField label="Type Name" value={data.typeName} testId="wt-typeName" />}
                <InfoField label="Manufacturer" value={wt.manufacturer} testId="wt-manufacturer" />
                <InfoField label="Model" value={wt.model} testId="wt-model" />
                <InfoField label="Frame Material" value={wt.frameMaterial} testId="wt-frameMaterial" />
                <InfoField label="Operation Type" value={wt.operationType} testId="wt-operationType" />
                {wt.uValue && <InfoField label="U Value" value={`${wt.uValue} Btu/hr·ft²·°F`} testId="wt-uValue" />}
                {wt.cr && <InfoField label="CR" value={wt.cr} testId="wt-cr" />}
                <InfoField label="Interior Finish" value={wt.finishInterior} testId="wt-finishInterior" />
                <InfoField label="Exterior Finish" value={wt.finishExterior} testId="wt-finishExterior" />
                <InfoField label="Hardware" value={wt.hardware} testId="wt-hardware" />
              </div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mt-4 mb-2">Window Dimensions</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {(data.windowWidth_in || data.windowHeight_in) && (
                  <InfoField
                    label="Window Size"
                    value={`${fmtDim(data.windowWidth_in)} W × ${fmtDim(data.windowHeight_in)} H`}
                    testId="dim-window"
                  />
                )}
                {(data.roWidth_in || data.roHeight_in) && (
                  <InfoField
                    label="Rough Opening"
                    value={`${fmtDim(data.roWidth_in)} W × ${fmtDim(data.roHeight_in)} H`}
                    testId="dim-ro"
                  />
                )}
                {data.location && <InfoField label="Location" value={data.location} testId="dim-location" />}
              </div>
            </div>
            {/* Right: Picture or Placeholder — 1/2 (50%) */}
            <div className="flex flex-col items-center justify-start" data-testid="wt-picture-container">
              {data.windowTypePictureUrl ? (
                <>
                  <img
                    src={data.windowTypePictureUrl}
                    alt="Window Type"
                    className="w-full aspect-auto object-contain rounded-lg border bg-white"
                    data-testid="wt-picture"
                  />
                  <p className="text-[10px] text-slate-400 text-center mt-1.5">Window Type Picture</p>
                </>
              ) : (
                <div className="w-full aspect-square rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center" data-testid="wt-picture-placeholder">
                  <span className="text-sm font-medium text-slate-400">No Image Available</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Lites ── */}
        {lites.length > 0 && (
          <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="public-lites">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Lites</h2>
            <div className="space-y-4">
              {lites.map((lite, idx) => {
                const liteType = lite.liteType || 'glass';
                const isGlass = liteType === 'glass';
                const gl = lite.glassType;
                const mp = lite.metalPanelType;
                return (
                  <div key={idx} className="rounded-lg border border-slate-200 p-4 bg-slate-50/30" data-testid={`lite-${idx}`}>
                    <h3 className="text-xs font-semibold text-slate-700 uppercase mb-2">
                      Lite {idx + 1} ({isGlass ? 'Glass' : 'Metal Panel'})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                      {isGlass && gl && (
                        <>
                          <InfoField label="Glass Type" value={gl.name} testId={`lite-${idx}-glassName`} />
                          {gl.glassManufacturer && <InfoField label="Manufacturer" value={gl.glassManufacturer} testId={`lite-${idx}-glassMfr`} />}
                          {gl.glassBuild && <InfoField label="Build" value={gl.glassBuild} testId={`lite-${idx}-glassBuild`} />}
                          {gl.glassThickness_in && <InfoField label="Thickness" value={`${gl.glassThickness_in}"`} testId={`lite-${idx}-glassThk`} />}
                          {gl.glassTypes?.length > 0 && <InfoField label="Features" value={gl.glassTypes.join(', ')} testId={`lite-${idx}-glassFeatures`} />}
                        </>
                      )}
                      {!isGlass && mp && (
                        <>
                          <InfoField label="Metal Panel" value={mp.name} testId={`lite-${idx}-metalName`} />
                          {mp.manufacturer && <InfoField label="Manufacturer" value={mp.manufacturer} testId={`lite-${idx}-metalMfr`} />}
                          {mp.model && <InfoField label="Model" value={mp.model} testId={`lite-${idx}-metalModel`} />}
                          {mp.thickness && <InfoField label="Thickness" value={`${mp.thickness}"`} testId={`lite-${idx}-metalThk`} />}
                          {mp.colorCode && <InfoField label="Color" value={mp.colorCode} testId={`lite-${idx}-metalColor`} />}
                        </>
                      )}
                      {(lite.width_in || lite.height_in) && (
                        <InfoField label="Lite Size" value={`${fmtDim(lite.width_in)} × ${fmtDim(lite.height_in)}`} testId={`lite-${idx}-size`} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-6 mb-4">This is a read-only window card generated by Specard</p>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// Generic Division 8 Public Card (CW, SF, Door, Shower, Mirror)
// ═══════════════════════════════════════════════════════════════
function Division8GenericCard({ data }) {
  const entityType = data.entityType;
  const typeLabel = D8_LABELS[entityType] || entityType;
  const qrUrl = data.qrToken ? buildQrUrl(data.qrToken) : '';

  let dims = null;
  if (entityType === 'door') dims = data.doorWidth_in || data.doorHeight_in ? `${fmtDim(data.doorWidth_in)} W × ${fmtDim(data.doorHeight_in)} H` : null;
  else if (entityType === 'shower') dims = data.showerWidth_in || data.showerHeight_in ? `${fmtDim(data.showerWidth_in)} W × ${fmtDim(data.showerHeight_in)} H` : null;
  else if (entityType === 'mirror') dims = data.mirrorWidth_in || data.mirrorHeight_in ? `${fmtDim(data.mirrorWidth_in)} W × ${fmtDim(data.mirrorHeight_in)} H` : null;
  else if (entityType === 'cw-panel' || entityType === 'sf-panel') dims = data.width_in || data.height_in ? `${fmtDim(data.width_in)} W × ${fmtDim(data.height_in)} H` : null;

  return (
    <>
      <div className="bg-white border-b" data-testid="public-brand-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center">
          <Logo size="sm" showText={false} />
          <span className="text-sm font-semibold text-slate-700 ml-2">Specard Inc.</span>
        </div>
      </div>
      <div className="max-w-5xl mx-auto py-4 sm:py-6 px-4 sm:px-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="public-page-title">{typeLabel} Card</h1>
            <p className="text-base font-semibold text-orange-600 mt-1" data-testid="public-entity-tag">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide mr-2">{typeLabel}</span>
              {data.label}
            </p>
          </div>
          {qrUrl && (
            <div className="shrink-0 ml-4" data-testid="public-qr-code">
              <QRCodeSVG value={qrUrl} size={96} level="L" />
            </div>
          )}
        </div>

        {(data.projectName || data.projectNumber) && (
          <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="public-project-info">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Project Information</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
              <InfoField label="Project" value={data.projectName} testId="public-projectName" />
              {data.projectNumber && <InfoField label="Project Number" value={data.projectNumber} testId="public-projectNumber" />}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="d8-public-card">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">{typeLabel} Information</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            {data.typeName && <InfoField label={`${typeLabel} Type`} value={data.typeName} testId="d8-typeName" />}
            {dims && <InfoField label="Dimensions" value={dims} testId="d8-dimensions" />}
            {data.location && <InfoField label="Location" value={data.location} testId="d8-location" />}
            {(entityType === 'cw-panel' || entityType === 'sf-panel') && data.panelType && (
              <InfoField label="Panel Type" value={data.panelType === 'GL' ? 'Glass' : 'Metal'} testId="d8-panelType" />
            )}
            {data.subTypeName && <InfoField label={data.panelType === 'GL' ? 'Glass Type' : 'Metal Type'} value={data.subTypeName} testId="d8-subTypeName" />}
            {data.hardwareSetName && <InfoField label="Hardware Set" value={data.hardwareSetName} testId="d8-hardwareSet" />}
          </div>
          {data.notes && (
            <div className="mt-3 pt-3 border-t">
              <dt className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Notes</dt>
              <dd className="text-sm text-slate-900 mt-0.5 whitespace-pre-wrap">{data.notes}</dd>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6 mb-4">This is a read-only card generated by Specard</p>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// Division 26 Public Card — simplified inline view
// ═══════════════════════════════════════════════════════════════
function Division26PublicCard({ data }) {
  const typeLabel = D26_LABELS[data.entityType] || data.entityType;
  const name = data.name || data.tag || data.deviceTag || 'Unknown';
  const qrUrl = data.qrToken ? buildQrUrl(data.qrToken) : '';

  const fields = [];
  if (data.panelType || data.type) fields.push(['Type', data.panelType || data.type]);
  if (data.location) fields.push(['Location', data.location]);
  if (data.manufacturerName) fields.push(['Manufacturer', data.manufacturerName]);
  if (data.model) fields.push(['Model', data.model]);
  if (data.voltage) fields.push(['Voltage', data.voltage]);
  if (data.phase) fields.push(['Phase', data.phase]);
  if (data.busRating) fields.push(['Bus Rating', data.busRating]);
  if (data.mainBreaker) fields.push(['Main Breaker', data.mainBreaker]);
  if (data.lastMaintenanceDate) fields.push(['Last Maintenance', data.lastMaintenanceDate]);

  return (
    <>
      <div className="bg-white border-b" data-testid="public-brand-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center">
          <Logo size="sm" showText={false} />
          <span className="text-sm font-semibold text-slate-700 ml-2">Specard Inc.</span>
        </div>
      </div>
      <div className="max-w-5xl mx-auto py-4 sm:py-6 px-4 sm:px-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="public-page-title">{typeLabel} Card</h1>
            <p className="text-base font-semibold text-orange-600 mt-1" data-testid="d26-item-name">{name}</p>
          </div>
          {qrUrl && (
            <div className="shrink-0 ml-4" data-testid="public-qr-code">
              <QRCodeSVG value={qrUrl} size={96} level="L" />
            </div>
          )}
        </div>

        {(data.projectName || data.projectNumber) && (
          <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="public-project-info">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Project Information</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
              <InfoField label="Project" value={data.projectName} testId="public-projectName" />
              {data.projectNumber && <InfoField label="Project Number" value={data.projectNumber} testId="public-projectNumber" />}
              {data.projectAddress && <InfoField label="Address" value={data.projectAddress} testId="public-projectAddress" />}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4" data-testid="d26-public-card">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">{typeLabel} Information</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            {fields.map(([label, value]) => (
              <InfoField key={label} label={label} value={value} testId={`d26-${label.toLowerCase().replace(/\s/g, '-')}`} />
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6 mb-4">This is a read-only card generated by Specard</p>
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// Main QR Page — resolves and renders in-place (no redirect)
// ═══════════════════════════════════════════════════════════════
export default function QRResolverPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setError('No QR token provided.'); setLoading(false); return; }
    let cancelled = false;
    axios.get(`${API}/api/public/qr/${token}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setError('This QR code is invalid or could not be found.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="qr-resolver-loading">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="qr-resolver-error">
      <div className="bg-white border-b px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2">
          <Logo size="sm" showText={false} />
          <span className="text-sm font-semibold text-slate-700">Specard Inc.</span>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-700">QR Code Not Found</h2>
          <p className="text-sm text-slate-400 mt-1">{error}</p>
        </div>
      </div>
    </div>
  );

  const entityType = data?.entityType;
  const isD7 = entityType in D7_LABELS;
  const isD26 = entityType in D26_LABELS;
  const isWindow = entityType === 'window';
  const isD8Other = entityType in D8_LABELS && !isWindow;
  const typeLabel = D7_LABELS[entityType] || D8_LABELS[entityType] || D26_LABELS[entityType] || entityType;

  // ── Window gets its own rich component ──
  if (isWindow) return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="public-qr-page">
      <WindowPublicCard data={data} />
    </div>
  );

  // ── Other Division 8 (generic) ──
  if (isD8Other) return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="public-qr-page">
      <Division8GenericCard data={data} />
    </div>
  );

  // ── Division 26 ──
  if (isD26) return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="public-qr-page">
      <Division26PublicCard data={data} />
    </div>
  );

  // ── Division 7 ──
  if (isD7) return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="public-qr-page">
      <div className="bg-white border-b" data-testid="public-brand-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size="sm" showText={false} />
            <span className="text-sm font-semibold text-slate-700">Specard Inc.</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="public-print-btn">
            <Printer className="h-4 w-4 mr-1.5" />Print
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-4 sm:py-6 px-4 sm:px-6">
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="public-page-title">Review</h1>
          <p className="text-sm text-slate-500 mt-1">
            <span className="font-medium text-slate-700" data-testid="public-product-name">{data.productNameModel}</span>
            {' · '}{typeLabel} · {data.manufacturerName || '—'}
            {data.projectName ? ` · ${data.projectName}` : ''}
          </p>
        </div>
        {getProductImage(data) && (
          <div className="mb-4">
            <img src={getProductImage(data)} alt={data.productNameModel} className="w-full max-h-64 sm:max-h-80 object-contain rounded-lg border bg-white" />
          </div>
        )}
        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 overflow-hidden">
          {entityType === 'thermal-insulation' && <ProductPrintView product={data} project={{ name: data.projectName, number: data.projectNumber }} />}
          {entityType === 'air-barrier' && <AirBarrierPrintView product={data} project={{ name: data.projectName, number: data.projectNumber }} />}
          {entityType === 'weather-barrier' && <WeatherBarrierPrintViewInline product={data} project={{ name: data.projectName, number: data.projectNumber }} />}
          {entityType === 'roofing' && <RoofingPrintView product={data} project={{ name: data.projectName, number: data.projectNumber }} />}
          {entityType === 'flashing' && <FlashingPrintView product={data} project={{ name: data.projectName, number: data.projectNumber }} />}
          {entityType === 'joint-protection' && <JointProtectionPrintViewInline product={data} project={{ name: data.projectName, number: data.projectNumber }} />}
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">This is a read-only review generated by Specard</p>
      </div>
    </div>
  );

  return null;
}
